import {mean} from 'lodash';

import {trackPanelQuickAdd} from '../../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../../state/hooks';
import * as PanelBankConfigActions from '../../../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useSortAlphabetically} from '../../Settings/hooks';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawerContextProvider';
import {usePanelsQuickAddContext} from '../PanelsQuickAddContext';
import {type MultiMetricPanelsMap} from './useMultiMetricPanelsMap';

export const useCreateMultiMetricPanels = (
  regexInputVal: string, // for analytics
  panelsToAdd: MultiMetricPanelsMap,
  isDisabled: boolean,
  shouldRemoveAutoPanels: boolean
) => {
  const dispatch = useDispatch();
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const sortAlphabetically = useSortAlphabetically();
  const {closeQuickAdd} = usePanelsQuickAddContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();

  return () => {
    if (isDisabled) {
      return;
    }

    const notYetAdded = Array.from(panelsToAdd.entries()).filter(
      ([regex, {isAlreadyAdded}]) => !isAlreadyAdded
    );
    const panelSpecs = notYetAdded
      .map(([regex, {defaultSection}]) => ({
        type: 'regex-panel' as const,
        keyType: 'number' as const,
        metricRegex: regex,
        defaultSection,
        isMergedPanel: shouldRemoveAutoPanels,
      }))
      .map(spec => ({key: spec.metricRegex, spec}));
    dispatch(
      PanelBankConfigActions.addPanelsBySpec(
        panelBankConfigRef,
        panelSpecs,
        sortAlphabetically
      )
    );
    closeQuickAdd();

    // analytics
    const metricCounts = notYetAdded.map(entry => entry[1].metrics.length);
    trackPanelQuickAdd({
      action: 'Multi-metric panels added',
      scope: targetSectionRef ? 'section' : 'global',
      metricRegex: regexInputVal,
      numPanelsAdded: panelSpecs.length,
      numMetricsMatchedMin: Math.min(...metricCounts),
      numMetricsMatchedMean: mean(metricCounts),
      numMetricsMatchedMax: Math.max(...metricCounts),
      removeAutoPanels: shouldRemoveAutoPanels,
    });
  };
};
